import axios from 'axios';
import { getBaseUrl } from './base-url.js';
import { checkEnvironmentSafety } from './environment-safety.js';
import { monitoredRequest, sanitizeEndpoint, } from '@repo/monitoring';
/**
 * Creates an API client with a configured request function.
 * @param config - The configuration for the API client.
 * @returns An object containing the `request` function.
 */
export function createApiClient(config) {
    const resolvedBaseUrl = getBaseUrl(config);
    /*
     * Run environment safety check on client initialization.
     * This logs warnings/errors if the configuration appears to be misconfigured
     * (e.g., preview app pointing to production API).
     */
    if (typeof window !== 'undefined') {
        // Only run in browser context where we can check the origin
        checkEnvironmentSafety({
            environment: config.environment,
            apiUrl: resolvedBaseUrl,
            appOrigin: window.location?.origin,
        });
    }
    else {
        // In SSR/Node context, check without origin
        checkEnvironmentSafety({
            environment: config.environment,
            apiUrl: resolvedBaseUrl,
        });
    }
    const request = async (method, url, requireAuth, data, customHeaders, monitoringMetadata) => {
        const headers = {
            'Content-Type': 'application/json',
            ...customHeaders,
        };
        if (requireAuth) {
            const token = await config.getToken();
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            else {
                // If auth is required but no token is available,
                // it's likely an immediate unauthenticated state.
                // The request will probably fail with 401, which will be handled below.
                console.warn(`[API Client] Auth required for ${method} ${url}, but no token available.`);
            }
        }
        if (config.getImpersonateId) {
            const impersonateId = await config.getImpersonateId();
            if (impersonateId) {
                headers['X-Impersonate-Id'] = impersonateId;
            }
        }
        // Only send X-App-Origin for authenticated requests to avoid CORS preflight
        // failures on public endpoints (e.g., /auth/login) when the frontend and API
        // are served from different origins during local development or preview.
        if (config.getAppOrigin) {
            const appOrigin = await config.getAppOrigin();
            if (appOrigin) {
                headers['X-App-Origin'] = appOrigin;
            }
        }
        const attemptRequest = async (requestHeaders) => {
            const resp = await axios({
                baseURL: resolvedBaseUrl,
                url,
                method,
                headers: requestHeaders,
                params: method.toUpperCase() === 'GET' ? data : undefined,
                data: method.toUpperCase() !== 'GET' ? data : undefined,
            });
            return resp.data;
        };
        // Execute request with monitoring
        return monitoredRequest(async () => {
            try {
                return await attemptRequest(headers);
            }
            catch (error) {
                // If this is an unauthenticated request with X-App-Origin and we hit a network/CORS
                // error (no response received), retry once without the custom header.
                if ('X-App-Origin' in headers &&
                    !error?.response &&
                    !error?.__tinyhealthRetried) {
                    const { 'X-App-Origin': _removed, ...headersWithoutOrigin } = headers;
                    try {
                        // Mark error to prevent infinite retry loops in extremely rare edge cases
                        error.__tinyhealthRetried = true;
                        return await attemptRequest(headersWithoutOrigin);
                    }
                    catch (retryErr) {
                        // If retry also fails, throw original retry error for context
                        throw retryErr;
                    }
                }
                const axiosError = error;
                if (axiosError.isAxiosError && axiosError.response) {
                    const { status } = axiosError.response;
                    if (status === 401) {
                        if (config.onUnauthenticated) {
                            await config.onUnauthenticated(axiosError);
                            // If onUnauthenticated handles the error (e.g., by redirecting),
                            // we might not want to re-throw, or re-throw a specific "handled" error.
                            // For now, we'll re-throw to let the caller decide.
                        }
                        // Re-throw so the specific calling Auth/API method can potentially transform it
                        // or the application's React Query onError can handle it.
                        throw axiosError;
                    }
                    else if (status === 403) {
                        if (config.onForbidden) {
                            await config.onForbidden(axiosError);
                        }
                        else if (config.showErrorToast) {
                            // Generic fallback toast if no specific onForbidden handler
                            config.showErrorToast(axiosError.response.data?.message ||
                                'You do not have permission to perform this action.');
                        }
                        // Similar to 401, re-throw to allow further handling.
                        throw axiosError;
                    }
                }
                // For non-Axios errors, network errors, or unhandled Axios error status codes
                throw error;
            }
        }, {
            endpoint: sanitizeEndpoint(url),
            method: method.toUpperCase(),
            tags: monitoringMetadata?.tags,
            context: monitoringMetadata?.context,
        }, 
        // Response extractor for monitoring
        () => {
            return {
                success: true,
                size: undefined, // We don't have direct access to response size with axios
            };
        });
    };
    return { request };
}
//# sourceMappingURL=client.js.map